import type {
  DashboardData,
  Language,
  PikbDashboardData,
  PikbSubmission,
  StoredPikbSubmission,
  StoredSubmission,
  SurveyEvent,
  SurveySubmission,
} from "../types";

const SUBMISSIONS_KEY = "putatan_survey_submissions";
const EVENTS_KEY = "putatan_survey_events";
const SESSION_KEY = "putatan_survey_session";
const PIKB_SUBMISSIONS_KEY = "pikb_survey_submissions";
const PIKB_EVENTS_KEY = "pikb_survey_events";
const PIKB_SESSION_KEY = "pikb_survey_session";

function readJson<T>(key: string, fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

export function getSessionKey() {
  const existing = window.localStorage.getItem(SESSION_KEY);
  if (existing) return existing;

  const key = crypto.randomUUID();
  window.localStorage.setItem(SESSION_KEY, key);
  return key;
}

export function resetSessionKey() {
  window.localStorage.removeItem(SESSION_KEY);
  return getSessionKey();
}

export function getPikbSessionKey() {
  const existing = window.localStorage.getItem(PIKB_SESSION_KEY);
  if (existing) return existing;

  const key = crypto.randomUUID();
  window.localStorage.setItem(PIKB_SESSION_KEY, key);
  return key;
}

export function resetPikbSessionKey() {
  window.localStorage.removeItem(PIKB_SESSION_KEY);
  return getPikbSessionKey();
}

export function storeLocalSubmission(
  submission: SurveySubmission,
  language: Language,
) {
  const submissions = readJson<StoredSubmission[]>(SUBMISSIONS_KEY, []);
  const stored: StoredSubmission = {
    ...submission,
    id: crypto.randomUUID(),
    created_at: new Date().toISOString(),
    language,
    user_agent: navigator.userAgent,
    metadata: { source: "local-preview" },
  };
  writeJson(SUBMISSIONS_KEY, [stored, ...submissions]);
  return stored;
}

export function storeLocalEvent(event: SurveyEvent) {
  const events = readJson<SurveyEvent[]>(EVENTS_KEY, []);
  writeJson(EVENTS_KEY, [
    {
      ...event,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
    },
    ...events,
  ]);
}

export function storeLocalPikbSubmission(
  submission: PikbSubmission,
  language: Language,
) {
  const submissions = readJson<StoredPikbSubmission[]>(PIKB_SUBMISSIONS_KEY, []);
  const stored: StoredPikbSubmission = {
    ...submission,
    id: crypto.randomUUID(),
    created_at: new Date().toISOString(),
    language,
    user_agent: navigator.userAgent,
    metadata: { source: "local-preview" },
  };
  writeJson(PIKB_SUBMISSIONS_KEY, [stored, ...submissions]);
  return stored;
}

export function storeLocalPikbEvent(event: SurveyEvent) {
  const events = readJson<SurveyEvent[]>(PIKB_EVENTS_KEY, []);
  writeJson(PIKB_EVENTS_KEY, [
    {
      ...event,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
    },
    ...events,
  ]);
}

export function deleteLocalPikbSubmission(id: string) {
  const submissions = readJson<StoredPikbSubmission[]>(PIKB_SUBMISSIONS_KEY, []);
  writeJson(
    PIKB_SUBMISSIONS_KEY,
    submissions.filter((submission) => submission.id !== id),
  );
}

export function getLocalDashboardData(): DashboardData {
  const submissions = readJson<StoredSubmission[]>(SUBMISSIONS_KEY, []);
  const events = readJson<SurveyEvent[]>(EVENTS_KEY, []);

  if (submissions.length || events.length) {
    return { submissions, events };
  }

  const today = new Date();
  const daysAgo = (days: number) => {
    const date = new Date(today);
    date.setDate(today.getDate() - days);
    return date.toISOString();
  };

  const seed: StoredSubmission[] = [
    {
      id: "demo-1",
      created_at: daysAgo(0),
      language: "ms",
      gender: "female",
      age_group: "dewasa_pertengahan_40_59",
      employment_status: "private_sector",
      nama_kampung: "Kampung Sailan",
      mukim: "Bandar Putatan",
      main_issue: "Kesesakan trafik pada waktu puncak dan keadaan jalan yang perlu ditambah baik.",
      development_suggestion: "Naik taraf jalan utama, tambah lampu jalan dan kemudahan pejalan kaki.",
    },
    {
      id: "demo-2",
      created_at: daysAgo(1),
      language: "ms",
      gender: "male",
      age_group: "belia_15_30",
      employment_status: "gig_worker",
      nama_kampung: "Kampung Serigai",
      mukim: "Papason",
      main_issue: "Peluang pekerjaan untuk belia masih terhad.",
      development_suggestion: "Wujudkan pusat latihan kemahiran digital dan sokongan usahawan muda.",
    },
    {
      id: "demo-3",
      created_at: daysAgo(2),
      language: "en",
      gender: "female",
      age_group: "warga_emas_60_plus",
      employment_status: "homemaker",
      nama_kampung: "Taman Lok Kawi Perdana",
      mukim: "Tanjung Keramat",
      main_issue: "Public facilities need better maintenance.",
      development_suggestion: "More family recreation areas and safer community spaces.",
    },
  ];

  const demoEvents: SurveyEvent[] = [
    ...seed.map((submission) => ({
      id: `start-${submission.id}`,
      session_key: `session-${submission.id}`,
      event_type: "started" as const,
      language: submission.language,
      created_at: submission.created_at,
    })),
    ...seed.map((submission) => ({
      id: `completed-${submission.id}`,
      session_key: `session-${submission.id}`,
      event_type: "completed" as const,
      language: submission.language,
      created_at: submission.created_at,
    })),
    {
      id: "demo-dropoff",
      session_key: "session-dropoff",
      event_type: "started",
      language: "ms",
      created_at: daysAgo(0),
    },
  ];

  return { submissions: seed, events: demoEvents };
}

export function getLocalPikbDashboardData(): PikbDashboardData {
  const submissions = readJson<StoredPikbSubmission[]>(
    PIKB_SUBMISSIONS_KEY,
    [],
  );
  const events = readJson<SurveyEvent[]>(PIKB_EVENTS_KEY, []);

  if (submissions.length || events.length) {
    return { submissions, events };
  }

  const today = new Date();
  const daysAgo = (days: number) => {
    const date = new Date(today);
    date.setDate(today.getDate() - days);
    return date.toISOString();
  };

  const seed: StoredPikbSubmission[] = [
    {
      id: "pikb-demo-1",
      created_at: daysAgo(0),
      language: "ms",
      zone: "beaufort",
      category: "makanan_kesihatan_tradisional",
      leader_name: "Siti Aminah Abdullah",
      leader_ic: "900101125678",
      leader_phone: "0123456789",
      leader_address: "Kg. Weston, Beaufort",
      members: [
        {
          name: "Nurul Huda",
          ic_number: "920202125678",
          phone: "0133456789",
        },
      ],
      innovation_title: "Herba Pek Mudah Alih",
      innovation_summary:
        "Inovasi ini membungkus ramuan herba tempatan dalam pek sukatan kecil yang mudah digunakan untuk kegunaan keluarga dan komuniti.",
    },
    {
      id: "pikb-demo-2",
      created_at: daysAgo(1),
      language: "ms",
      zone: "lahad_datu",
      category: "keselamatan_kemakmuran_komuniti",
      leader_name: "John Martin",
      leader_ic: "880303125678",
      leader_phone: "0143456789",
      leader_address: "Kg. Limbawang, Beaufort",
      members: [],
      innovation_title: "Lampu Komuniti Tenaga Solar",
      innovation_summary:
        "Cadangan ini menggunakan lampu solar mudah pasang bagi kawasan laluan kampung yang gelap untuk meningkatkan keselamatan penduduk pada waktu malam.",
    },
    {
      id: "pikb-demo-3",
      created_at: daysAgo(2),
      language: "ms",
      zone: "ranau",
      category: "seni_kraftangan_muzik_tradisional",
      leader_name: "Maria Joseph",
      leader_ic: "950404125678",
      leader_phone: "0163456789",
      leader_address: "Kg. Padas Damit, Beaufort",
      members: [
        {
          name: "Linda Paulus",
          ic_number: "960505125678",
          phone: "0173456789",
        },
        {
          name: "Raymond Jalius",
          ic_number: "970606125678",
          phone: "0183456789",
        },
      ],
      innovation_title: "Kit Kraftangan Motif Tempatan",
      innovation_summary:
        "Kit ini membantu belia mempelajari motif tradisional melalui bahan ringkas, panduan bergambar dan contoh produk kecil yang boleh dijual.",
    },
  ];

  const demoEvents: SurveyEvent[] = [
    ...seed.map((submission) => ({
      id: `start-${submission.id}`,
      session_key: `session-${submission.id}`,
      event_type: "started" as const,
      language: submission.language,
      created_at: submission.created_at,
    })),
    ...seed.map((submission) => ({
      id: `completed-${submission.id}`,
      session_key: `session-${submission.id}`,
      event_type: "completed" as const,
      language: submission.language,
      created_at: submission.created_at,
    })),
  ];

  return { submissions: seed, events: demoEvents };
}
